import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './LunchRouletteAdminPanel.module.css';
import { 
  lunchRouletteApi, 
  AdminPanelData,
  LunchRouletteGroup,
  UnmatchedUser,
  CreateGroupRequest,
  AddMemberRequest
} from '../../services/lunchRouletteApi';
import { useUserContext, useUserRoles } from '../../contexts/UserContext';
import { userApi } from '../../services/userApi';
import { useBranding } from '../../hooks/useBranding';

const LunchRouletteAdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { rouletteId } = useParams<{ rouletteId: string }>();
  const { user } = useUserContext();
  const userRoles = useUserRoles();
  const { appName } = useBranding();
  
  // Data states
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<AdminPanelData | null>(null);
  const [directLink, setDirectLink] = useState<string>('');
  
  // Selection states
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedGroupDisplayNumber, setSelectedGroupDisplayNumber] = useState<number | null>(null);
  
  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showUnmatchedUsersModal, setShowUnmatchedUsersModal] = useState<boolean>(false);
  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showRegisteredUsersModal, setShowRegisteredUsersModal] = useState<boolean>(false);
  const [showRegisterUserModal, setShowRegisterUserModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'add'>('create');
  
  // User management states
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [registeredUserIds, setRegisteredUserIds] = useState<Set<string>>(new Set());
  const [userSearchInput, setUserSearchInput] = useState<string>('');
  const [filteredUnregisteredUsers, setFilteredUnregisteredUsers] = useState<any[]>([]);
  
  // Confirmation state
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  
  // Comment state
  const [currentComment, setCurrentComment] = useState<{ userName: string, comment: string } | null>(null);
  
  // Processing states
  const [generating, setGenerating] = useState<boolean>(false);
  const [creatingGroup, setCreatingGroup] = useState<boolean>(false);
  const [addingMember, setAddingMember] = useState<boolean>(false);
  const [deletingGroup, setDeletingGroup] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [sendingMatches, setSendingMatches] = useState<boolean>(false);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [showSendMatchesModal, setShowSendMatchesModal] = useState<boolean>(false);
  const [downloadingInvitation, setDownloadingInvitation] = useState<boolean>(false);
  
  // Invitation modal states
  const [showInvitationModal, setShowInvitationModal] = useState<boolean>(false);
  const [additionalInfoDe, setAdditionalInfoDe] = useState<string>('');
  const [additionalInfoEn, setAdditionalInfoEn] = useState<string>('');

  // Refs for error animations
  const createGroupErrorRef = useRef<HTMLDivElement>(null);
  const addMembersErrorRef = useRef<HTMLDivElement>(null);

  // Helper function to extract error message from error objects
  const getErrorMessage = (error: any): string => {
    if (error instanceof Error) {
      return error.message;
    }
    
    if (typeof error === 'object' && error !== null) {
      // Try to extract message from axios or custom API error
      if ('message' in error) return error.message;
      if ('response' in error && error.response?.data?.detail) return error.response.data.detail;
      if ('response' in error && error.response?.data?.message) return error.response.data.message;
    }
    
    return 'Unknown error occurred';
  };

  // Check if user has admin permissions
  const isAdmin = (): boolean => {
    return userRoles.some(role => ['Admin', 'Lunch-Roulette Admin'].includes(role));
  };

  // Fetch admin panel data
  const fetchAdminData = async () => {
    if (!rouletteId) return;

    try {
      setLoading(true);
  const adminData = await lunchRouletteApi.getAdminPanelData(rouletteId);
      setData(adminData);
      
      // Update registered user IDs
  const registeredIds = new Set<string>();
      adminData.matches.forEach(group => {
        group.members.forEach(member => {
          registeredIds.add(member.User_ID);
        });
      });
      adminData.unmatched.forEach(user => {
  registeredIds.add(user.User_ID);
      });
      setRegisteredUserIds(registeredIds);
      
      // Set direct registration link
  setDirectLink(lunchRouletteApi.getDirectRegisterLink(rouletteId));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to load admin data: ${errorMessage}`);
      console.error('Error fetching admin data:', error);
      if ((error as any)?.message?.includes('403') || (error as any)?.message?.includes('Admin access required')) {
        navigate('/lunch-roulette');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load all users for registration
  const loadAllUsers = async () => {
    try {
      const users = await userApi.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load user list');
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Copy registration link to clipboard with formatted text as hyperlink
  const handleCopyRegisterLink = async () => {
    if (!data || !rouletteId) return;

    try {
      // Use the direct register link from API
  const registerUrl = lunchRouletteApi.getDirectRegisterLink(rouletteId);
      const lunchDate = new Date(data.roulette.lunch_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Format: {AppName} 🍽️ Lunch Roulette at {date} | Register Now
      const linkText = `${appName} 🍽️ Lunch Roulette at ${lunchDate} | Register Now`;
      
      // Create HTML and plain text versions
      const htmlContent = `<a href="${registerUrl}">${linkText}</a>`;
      const plainText = `${linkText}\n${registerUrl}`;
      
      // Copy as rich text (HTML) and fallback to plain text
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' })
      });
      
      await navigator.clipboard.write([clipboardItem]);
      
      toast.success('📋 Link copied to clipboard!', {
        autoClose: 2000,
        position: 'top-center'
      });
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Failed to copy link');
    }
  };

  // Generate matches
  const handleGenerateMatches = async () => {
    if (!rouletteId) return;

    try {
      setGenerating(true);
  const response = await lunchRouletteApi.generateMatches(rouletteId);
      toast.success(response.message);
      setShowGenerateModal(false);
      await fetchAdminData(); // Refresh data
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to generate matches: ${errorMessage}`);
    } finally {
      setGenerating(false);
    }
  };

  // Create a new group with selected users
  const handleCreateGroup = async () => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }
    
    // Validate that users are selected
    if (selectedUsers.length === 0) {
      // Show error animation to highlight the issue
      if (createGroupErrorRef.current) {
        createGroupErrorRef.current.classList.add(styles.shakeError);
        setTimeout(() => {
          if (createGroupErrorRef.current) {
            createGroupErrorRef.current.classList.remove(styles.shakeError);
          }
        }, 600);
      }
      
      toast.error('Please select at least one user to create a group');
      return;
    }

    try {
      // Set processing state to show loader
      setCreatingGroup(true);
      
      // Prepare request data
      const requestData: CreateGroupRequest = {
        selected_users: selectedUsers
      };
      
      // Call API to create group
  const response = await lunchRouletteApi.createGroup(rouletteId, requestData);
      
      // Calculate the next display number (current groups count + 1)
      const newDisplayNumber = data ? data.matches.length + 1 : 1;
      
      // Show success message with the display number
      toast.success(`Group #${newDisplayNumber} created successfully`);
      
      // Close the modal and reset UI state
      setShowUnmatchedUsersModal(false);
      setSelectedUsers([]); // Clear selection
      
      // Refresh admin panel data to show the new group
      await fetchAdminData();
    } catch (error: any) {
      // Extract and display error message
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to create group: ${errorMessage}`);
      console.error('Group creation error:', error);
    } finally {
      // Reset processing state
      setCreatingGroup(false);
    }
  };

  // Add members to an existing group
  const handleAddMember = async () => {
    if (!rouletteId || !selectedGroup) {
      toast.error('Please select a group to add members to');
      return;
    }

    // Validate that users are selected
    if (selectedUsers.length === 0) {
      // Show error animation to highlight the issue
      if (addMembersErrorRef.current) {
        addMembersErrorRef.current.classList.add(styles.shakeError);
        setTimeout(() => {
          if (addMembersErrorRef.current) {
            addMembersErrorRef.current.classList.remove(styles.shakeError);
          }
        }, 600);
      }
      
      toast.error('Please select at least one user to add to the group');
      return;
    }

    try {
      // Set processing state to show loader
      setAddingMember(true);
      
      // Prepare request data with the correct format expected by the API
      const requestData: AddMemberRequest = {
        Group_ID: selectedGroup,
        selected_users: selectedUsers
      };
      
      // Call API to add members to group
  const response = await lunchRouletteApi.addMembersToGroup(rouletteId, requestData);
      
      // Show success message
      toast.success(response.message || 'Members added to group successfully');
      
      // Close the modal and reset UI state
      setShowUnmatchedUsersModal(false);
      setSelectedUsers([]); // Clear selection
      setSelectedGroup(null); // Clear selected group
      
      // Refresh admin panel data to show updated groups
      await fetchAdminData();
    } catch (error: any) {
      // Extract and display error message
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to add members: ${errorMessage}`);
      console.error('Add member error:', error);
    } finally {
      // Reset processing state
      setAddingMember(false);
    }
  };

  // Remove a member from a group with confirmation toast
  const handleRemoveMember = async (groupId: string, userId: string) => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    // Use custom confirmation modal instead of window.confirm
    setConfirmAction({
      title: 'Remove Member',
      message: 'Are you sure you want to remove this member from the group?',
      onConfirm: async () => {
        try {
          // Show loading indicator
          setDeletingMemberId(userId);
          
          // Call API to remove the member
          await lunchRouletteApi.removeMemberFromGroup(
            rouletteId,
            groupId,
            userId
          );
          
          // Show success message
          toast.success('Member removed successfully');
          
          // Refresh admin panel data to show updated groups
          await fetchAdminData();
        } catch (error: any) {
          // Extract and display error message
          const errorMessage = getErrorMessage(error);
          toast.error(`Failed to remove member: ${errorMessage}`);
          console.error('Remove member error:', error);
        } finally {
          // Reset loading indicator
          setDeletingMemberId(null);
        }
      }
    });
    setShowConfirmModal(true);
  };

  // Delete a group
  const handleDeleteGroup = async () => {
    if (!rouletteId || !selectedGroup) {
      toast.error('Please select a group to delete');
      return;
    }

    try {
      // Set processing state to show loader
      setDeletingGroup(true);
      
      // Call API to delete the group
  const response = await lunchRouletteApi.deleteGroup(rouletteId, selectedGroup);
      
      // Show success message with display number
      toast.success(`Group #${selectedGroupDisplayNumber} deleted successfully`);
      
      // Close modal and reset UI state
      setShowDeleteGroupModal(false);
      setSelectedGroup(null);
      setSelectedGroupDisplayNumber(null);
      
      // Refresh admin panel data to show updated groups
      await fetchAdminData();
    } catch (error: any) {
      // Extract and display error message
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to delete group: ${errorMessage}`);
      console.error('Delete group error:', error);
    } finally {
      // Reset processing state
      setDeletingGroup(false);
    }
  };

  // Register a user for the roulette (Admin)
  const handleRegisterUser = async (userId: string) => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    try {
  const response = await lunchRouletteApi.adminRegisterUser(rouletteId, userId);
      toast.success(response.message);
      
      // Close modal and refresh data
      setShowRegisterUserModal(false);
      setUserSearchInput('');
      await fetchAdminData();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to register user: ${errorMessage}`);
      console.error('Register user error:', error);
    }
  };

  // Unregister a user from the roulette (Admin)
  const handleUnregisterUser = async (userId: string) => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    // Use custom confirmation modal
    setConfirmAction({
      title: 'Unregister User',
      message: 'Are you sure you want to unregister this user? This will also remove them from any groups they are in.',
      onConfirm: async () => {
        try {
          const response = await lunchRouletteApi.adminUnregisterUser(rouletteId, userId);
          toast.success(response.message);
          
          // Refresh data
          await fetchAdminData();
        } catch (error: any) {
          const errorMessage = getErrorMessage(error);
          toast.error(`Failed to unregister user: ${errorMessage}`);
          console.error('Unregister user error:', error);
        }
      }
    });
    setShowConfirmModal(true);
  };

  // Export data to Excel
  const handleExport = async () => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    try {
      // Set exporting state to show loader
      setExporting(true);
      
      // Call API to get the Excel file
  const blob = await lunchRouletteApi.exportToExcel(rouletteId);
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lunch-roulette-${rouletteId}.xlsx`);
      
      // Append to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      // Show success message
      toast.success('Export successful. The file should download automatically.');
    } catch (error: any) {
      // Extract and display error message
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to export data: ${errorMessage}`);
      console.error('Export error:', error);
    } finally {
      // Reset exporting state
      setExporting(false);
    }
  };

  // Download invitation email
  const handleDownloadInvitation = async () => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    try {
      setDownloadingInvitation(true);
      
      // Call API to get the EML file
      const blob = await lunchRouletteApi.downloadInvitation(
  rouletteId,
        additionalInfoDe,
        additionalInfoEn
      );
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lunch-roulette-invitation-${rouletteId}.eml`);
      
      // Append to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      // Show success message and close modal
      toast.success('Invitation email downloaded successfully!');
      setShowInvitationModal(false);
      
      // Reset form
      setAdditionalInfoDe('');
      setAdditionalInfoEn('');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to download invitation: ${errorMessage}`);
      console.error('Download invitation error:', error);
    } finally {
      setDownloadingInvitation(false);
    }
  };

  // Send match notification emails
  const handleSendMatches = async () => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    // Check if there are any matches
    if (!data || data.matches.length === 0) {
      toast.error('No matches found. Please generate matches first.');
      return;
    }

    try {
      setSendingMatches(true);
      
  const response = await lunchRouletteApi.sendMatches(rouletteId);
      
      toast.success(
        `Successfully sent ${response.total_recipients} emails to ${response.total_groups} groups!`,
        { autoClose: 7000 }
      );
      
      if (response.failed_sends > 0) {
        toast.warning(
          `Warning: ${response.failed_sends} emails failed to send.`,
          { autoClose: 7000 }
        );
      }
      
      setShowSendMatchesModal(false);
      await fetchAdminData(); // Refresh data to update mail_log
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to send match emails: ${errorMessage}`);
      console.error('Send matches error:', error);
    } finally {
      setSendingMatches(false);
    }
  };

  // Send match notification emails for a specific group
  const handleSendGroupMatches = async (groupId: string, displayNumber: number) => {
    if (!rouletteId) {
      toast.error('Roulette ID is missing');
      return;
    }

    try {
  const response = await lunchRouletteApi.sendGroupMatches(rouletteId, groupId);
      
      const sentDate = new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
      
      toast.success(
        `Matches Sent! Emails sent to ${response.total_recipients} participants in 1 group on ${sentDate}`,
        { autoClose: 5000 }
      );
      
      if (response.failed_sends > 0) {
        toast.warning(
          `Warning: ${response.failed_sends} emails failed to send.`,
          { autoClose: 5000 }
        );
      }
      
      await fetchAdminData(); // Refresh data to update mail_sent status
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to send emails: ${errorMessage}`);
      console.error('Send group matches error:', error);
    }
  };

  // Toggle user selection for groups/members
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) // Remove if already selected
        : [...prev, userId]                // Add if not selected
    );
  };

  // Select all unmatched users
  const selectAllUnmatchedUsers = () => {
    if (!data) return;
    const allUserIds = data.unmatched.map(user => user.User_ID);
    setSelectedUsers(allUserIds);
  };

  // Clear all user selections
  const clearUserSelections = () => {
    setSelectedUsers([]);
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedUsers([]);
    setSelectedGroup(null);
  };

  // Initialize component
  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Admin access required');
      navigate('/lunch-roulette');
      return;
    }
    fetchAdminData();
    loadAllUsers();
  }, [rouletteId]);

  // Filter unregistered users based on search input
  useEffect(() => {
    if (!allUsers.length) return;

    const unregistered = allUsers.filter(user => !registeredUserIds.has(user.User_ID));
    
    if (!userSearchInput.trim()) {
      setFilteredUnregisteredUsers(unregistered);
      return;
    }

    const searchTerm = userSearchInput.toLowerCase();
    const filtered = unregistered.filter(user => 
      user.firstname?.toLowerCase().includes(searchTerm) ||
      user.lastname?.toLowerCase().includes(searchTerm) ||
      user.mail?.toLowerCase().includes(searchTerm) ||
      `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchTerm)
    );
    
    setFilteredUnregisteredUsers(filtered);
  }, [userSearchInput, allUsers, registeredUserIds]);

  // Öffne das Kommentar-Modal
  const openCommentModal = (userName: string, comment: string) => {
    setCurrentComment({ userName, comment });
    setShowCommentModal(true);
  };

  // Erstelle eine Funktion zum Öffnen des Unmatched Users Modals im richtigen Modus
  const openUnmatchedUsersModal = (mode: 'create' | 'add') => {
    setModalMode(mode);
    setShowUnmatchedUsersModal(true);
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Zeige einen Ladeindikator oder Fehlermeldung, wenn Daten nicht verfügbar sind
  if (!data) {
    // Falls Daten fehlen und kein Ladevorgang aktiv ist, zeige Toast-Nachricht
    if (!loading) {
      toast.error("Failed to load admin panel data. Please try again later.");
    }
    
    return (
      <div className="container mt-4 text-center">
        <div className={styles.loadingContainer}>
          {loading ? (
            <>
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5>Loading admin panel data...</h5>
            </>
          ) : (
            <>
              <i className="bi bi-exclamation-triangle-fill text-danger mb-3" style={{ fontSize: '2rem' }}></i>
              <h5>Failed to load data</h5>
              <button 
                className="btn btn-primary mt-3" 
                onClick={() => fetchAdminData()}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className={styles.breadcrumb}>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <button 
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => navigate('/lunch-roulette')}
            >
              Lunch Roulette
            </button>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Admin Panel - Roulette {rouletteId}
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className={styles.eventHeader}>
        <div className="row align-items-center">
          <div className="col-md-8">
            <h1 className={styles.eventTitle}>
              <i className="bi bi-shield-lock-fill me-3"></i>
              Lunch Roulette Admin Panel
            </h1>
            <p className={styles.eventSubtitle}>
              Lunch Date: {formatDate(data.roulette.lunch_date)}
            </p>
          </div>
          <div className="col-md-4 text-end">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/lunch-roulette')}
              title="Return to overview"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Roulette Configuration - Kompakt */}
      <div className="row g-3 mb-4">
        <div className="col-md-12">
          <div className={`${styles.statCard} card`}>
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-gear-fill me-2"></i>
                Roulette Configuration
              </h5>
              <div className="row g-3">
                {/* Statistics Row */}
                <div className="col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <i className="bi bi-people-fill text-primary fs-4 mb-1"></i>
                    <div className="fw-bold text-primary fs-4">{data.registered_count}</div>
                    <small className="text-muted">Registered Users</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <i className="bi bi-collection-fill text-success fs-4 mb-1"></i>
                    <div className="fw-bold text-success fs-4">{data.matches.length}</div>
                    <small className="text-muted">Groups Created</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <i className="bi bi-check-circle-fill text-info fs-4 mb-1"></i>
                    <div className="fw-bold text-info fs-4">{data.matches.reduce((sum, group) => sum + group.members.length, 0)}</div>
                    <small className="text-muted">Matched Users</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-2 bg-light rounded">
                    <i className="bi bi-exclamation-circle-fill text-warning fs-4 mb-1"></i>
                    <div className="fw-bold text-warning fs-4">{data.unmatched.length}</div>
                    <small className="text-muted">Unmatched Users</small>
                  </div>
                </div>
              </div>

              {/* Configuration Details */}
              <div className="row mt-3 g-2">
                <div className="col-md-4">
                  <small className="text-muted d-block">Group Size</small>
                  <strong>{data.roulette.group_size}</strong>
                </div>
                <div className="col-md-4">
                  <small className="text-muted d-block">Type</small>
                  <strong>{data.roulette.is_hybrid ? 'Hybrid (Teams)' : 'In-Person'}</strong>
                </div>
                <div className="col-md-4">
                  <small className="text-muted d-block">Registration Period</small>
                  <strong className="d-block" style={{fontSize: '0.85rem'}}>
                    {new Date(data.roulette.registration_start).toLocaleDateString()} - {new Date(data.roulette.registration_end).toLocaleDateString()}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Menu */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <h5 className="card-title mb-4">
                <i className="bi bi-lightning-fill me-2"></i>
                Actions
              </h5>
              
              <div className="row g-4">
                {/* Match Management */}
                <div className="col-md-4">
                  <h6 className="mb-3 text-muted">
                    <i className="bi bi-people-fill me-2"></i>
                    Match Management
                  </h6>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-warning text-start"
                      onClick={() => openUnmatchedUsersModal('create')}
                    >
                      <i className="bi bi-people me-2"></i>
                      View & Create Groups
                      <span className="badge bg-white text-warning ms-2">{data.unmatched.length}</span>
                    </button>
                    <button 
                      className="btn btn-primary text-start"
                      onClick={() => setShowGenerateModal(true)}
                    >
                      <i className="bi bi-shuffle me-2"></i>
                      Generate Matches
                    </button>
                  </div>
                </div>

                {/* Mails */}
                <div className="col-md-4">
                  <h6 className="mb-3 text-muted">
                    <i className="bi bi-envelope-fill me-2"></i>
                    Mails
                  </h6>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-success text-start"
                      onClick={() => setShowSendMatchesModal(true)}
                      disabled={data.matches.length === 0 || !data.matches.some(g => !g.mail_sent)}
                    >
                      <i className="bi bi-send-fill me-2"></i>
                      Send MatchMails to all Groups
                    </button>
                    <button 
                      className="btn btn-info text-start"
                      onClick={() => setShowInvitationModal(true)}
                    >
                      <i className="bi bi-download me-2"></i>
                      Download Invitation
                    </button>
                    <button 
                      className="btn btn-outline-primary text-start"
                      onClick={handleCopyRegisterLink}
                    >
                      <i className="bi bi-link-45deg me-2"></i>
                      Copy Register Link
                    </button>
                  </div>
                </div>

                {/* Export */}
                <div className="col-md-4">
                  <h6 className="mb-3 text-muted">
                    <i className="bi bi-file-earmark-arrow-down-fill me-2"></i>
                    Export
                  </h6>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-secondary text-start"
                      onClick={() => setShowExportModal(true)}
                    >
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Download Export
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Match Groups */}
      <div className="row" id="match-groups-section">
        <div className="col-12">
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center py-2">
              <h6 className="mb-0"><i className="bi bi-people-fill me-2"></i>Current Match Groups</h6>
              <span className="badge bg-light text-success">{data.matches.length} Groups</span>
            </div>
            <div className="card-body p-2">
              {data.matches.length > 0 ? (
                <div>
                  {data.matches.map((group, index) => (
                    <div key={group.Group_ID} className="border rounded mb-3 bg-white">
                      <div className="bg-light py-2 px-3 border-bottom">
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <div className="d-flex align-items-center">
                            <span className="badge bg-primary me-2">{group.members.length}/{data.roulette.group_size}</span>
                            <strong className="me-3">Group #{index + 1}</strong>
                            {group.mail_sent && (
                              <span 
                                className="badge bg-success" 
                                title={data.mail_log ? `Emails sent on ${new Date(data.mail_log.sent_at).toLocaleDateString()}` : 'Emails sent'}
                              >
                                <i className="bi bi-envelope-check-fill"></i>
                              </span>
                            )}
                          </div>
                          {/* Action Buttons in Header */}
                          <div className="d-flex gap-2">
                            {!group.mail_sent && (
                              <button 
                                className="btn btn-warning btn-sm"
                                onClick={() => handleSendGroupMatches(group.Group_ID, index + 1)}
                                title="Send match emails"
                              >
                                <i className="bi bi-envelope-fill"></i>
                              </button>
                            )}
                            {group.members.length < data.roulette.group_size && (
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  setSelectedGroup(group.Group_ID);
                                  setSelectedGroupDisplayNumber(index + 1);
                                  openUnmatchedUsersModal('add');
                                }}
                                title="Add user to group"
                              >
                                <i className="bi bi-person-plus"></i>
                              </button>
                            )}
                            <button 
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setSelectedGroup(group.Group_ID);
                                setSelectedGroupDisplayNumber(index + 1);
                                setShowDeleteGroupModal(true);
                              }}
                              title="Delete group"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">

                        {/* Members List */}
                        <div className="row g-2">
                            {group.members.map((member) => (
                              <div key={member.User_ID} className="col-md-6">
                                <div className="card bg-light">
                                  <div className="card-body p-2">
                                    <div className="d-flex align-items-start">
                                      {/* Profile Picture */}
                                      {member.picture ? (
                                        <img 
                                          className="rounded-circle me-2" 
                                          src={`data:image/png;base64,${member.picture}`} 
                                          alt={`${member.firstname} ${member.lastname}`} 
                                          style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover' }} 
                                        />
                                      ) : (
                                        <div 
                                          className="rounded-circle me-2 d-flex align-items-center justify-content-center bg-secondary text-white" 
                                          style={{ width: '2.5rem', height: '2.5rem', fontSize: '0.75rem' }}
                                        >
                                          <i className="bi bi-person-fill"></i>
                                        </div>
                                      )}
                                      
                                      {/* User Info */}
                                      <div className="flex-grow-1" style={{fontSize: '0.75rem', lineHeight: '1.4'}}>
                                        <strong className="d-block mb-1" style={{fontSize: '0.85rem'}}>
                                          {member.firstname} {member.lastname}
                                        </strong>
                                        {member.mail ? (
                                          <div className="text-muted">
                                            <i className="bi bi-envelope me-1"></i>{member.mail}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <i className="bi bi-envelope me-1"></i>-
                                          </div>
                                        )}
                                        {member.jobtitle ? (
                                          <div className="text-muted">
                                            <i className="bi bi-briefcase me-1"></i>{member.jobtitle}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <i className="bi bi-briefcase me-1"></i>-
                                          </div>
                                        )}
                                        {member.city_name ? (
                                          <div className="text-muted">
                                            <i className="bi bi-geo-alt me-1"></i>{member.city_name}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <i className="bi bi-geo-alt me-1"></i>-
                                          </div>
                                        )}
                                        {member.department ? (
                                          <div className="text-muted">
                                            <i className="bi bi-building me-1"></i>{member.department}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <i className="bi bi-building me-1"></i>-
                                          </div>
                                        )}
                                        {member.employee_group ? (
                                          <div className="text-muted">
                                            <i className="bi bi-people me-1"></i>{member.employee_group}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            <i className="bi bi-people me-1"></i>-
                                          </div>
                                        )}
                                        {member.comment && (
                                          <div className="text-muted mt-1 p-1 bg-white rounded border border-info">
                                            <i className="bi bi-chat-text-fill text-info me-1"></i>
                                            <small className="fst-italic">{member.comment}</small>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Action Buttons */}
                                      <div className="d-flex flex-column gap-1 ms-2">
                                        {group.members.length > 2 && (
                                          <button 
                                            className="btn btn-link p-0 text-danger"
                                            onClick={() => handleRemoveMember(group.Group_ID, member.User_ID)}
                                            disabled={deletingMemberId === member.User_ID}
                                            title="Remove member"
                                          >
                                            {deletingMemberId === member.User_ID ? (
                                              <span className="spinner-border spinner-border-sm"></span>
                                            ) : (
                                              <i className="bi bi-person-dash"></i>
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4">
                  <i className="bi bi-people text-muted mb-2" style={{ fontSize: '3rem' }}></i>
                  <h6 className="text-muted">No match groups found</h6>
                  <p className="text-muted small">Create groups manually or use Generate Matches</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selection Summary - show when users are selected */}
      {selectedUsers.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className={styles.selectionSummary}>
              <span>
                <i className="bi bi-check-circle-fill me-2"></i>
                {selectedUsers.length} users selected
              </span>
              <div>
                <button 
                  className="btn btn-sm btn-primary me-2"
                  onClick={() => openUnmatchedUsersModal('create')}
                >
                  <i className="bi bi-people-fill me-1"></i>
                  Create Group
                </button>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={clearSelections}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Matches Modal */}
      {showGenerateModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-shuffle me-2 text-primary"></i>
                  Generate Lunch Matches
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowGenerateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Generate new groups based on registered users and configuration
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="p-2 bg-light rounded text-center">
                      <small className="text-muted d-block">Registered</small>
                      <strong className="text-primary">{data.registered_count}</strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-2 bg-light rounded text-center">
                      <small className="text-muted d-block">Unmatched</small>
                      <strong className="text-warning">{data.unmatched.length}</strong>
                    </div>
                  </div>
                </div>
                <p className="text-muted small mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  Existing groups will be preserved
                </p>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowGenerateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleGenerateMatches}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shuffle me-1"></i>
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unmatched Users Modal */}
      {showUnmatchedUsersModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  {modalMode === 'create' ? (
                    <>
                      <i className="bi bi-person-dash me-2 text-warning"></i>
                      Unmatched Users
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-plus-fill me-2 text-primary"></i>
                      Add Users to Group #{selectedGroupDisplayNumber}
                    </>
                  )}
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowUnmatchedUsersModal(false)}
                ></button>
              </div>
              <div className="modal-body" ref={modalMode === 'create' ? createGroupErrorRef : addMembersErrorRef}>
                {modalMode === 'create' ? (
                  <div className="alert alert-info mb-3 py-2">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      Select <strong>2 or more</strong> users to create a match group (Recommended: {data.roulette.group_size})
                    </small>
                  </div>
                ) : (
                  <div className="alert alert-info mb-3 py-2">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      Current slots: {data.matches.find(g => g.Group_ID === selectedGroup)?.members.length || 0}/{data.roulette.group_size}
                    </small>
                  </div>
                )}
                
                {/* Register New User Button */}
                <button 
                  className="btn btn-success btn-sm w-100 mb-3"
                  onClick={() => {
                    setShowUnmatchedUsersModal(false);
                    setShowRegisterUserModal(true);
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Register New User
                </button>

                {/* User List */}
                <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="mb-3">
                  {data.unmatched.map(user => (
                    <div 
                      key={user.User_ID} 
                      className={`mb-2 p-2 rounded border ${selectedUsers.includes(user.User_ID) ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-light'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleUserSelection(user.User_ID)}
                    >
                      <div className="d-flex align-items-start">
                        {/* Checkbox */}
                        <div className="form-check me-2">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id={`user_${user.User_ID}`}
                            checked={selectedUsers.includes(user.User_ID)}
                            onChange={() => {}}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>

                        {/* Profile Picture */}
                        {user.picture ? (
                          <img 
                            className="rounded-circle me-2" 
                            src={`data:image/png;base64,${user.picture}`} 
                            alt={`${user.firstname} ${user.lastname}`} 
                            style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover' }} 
                          />
                        ) : (
                          <div 
                            className="rounded-circle me-2 d-flex align-items-center justify-content-center bg-secondary text-white" 
                            style={{ width: '2.5rem', height: '2.5rem', fontSize: '0.75rem' }}
                          >
                            <i className="bi bi-person-fill"></i>
                          </div>
                        )}

                        {/* User Info */}
                        <div className="flex-grow-1">
                          <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>
                            {user.firstname} {user.lastname}
                          </div>
                          <div style={{fontSize: '0.75rem', lineHeight: '1.4'}}>
                            {user.mail ? (
                              <div className="text-muted">
                                <i className="bi bi-envelope me-1"></i>{user.mail}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-envelope me-1"></i>-
                              </div>
                            )}
                            {user.jobtitle ? (
                              <div className="text-muted">
                                <i className="bi bi-briefcase me-1"></i>{user.jobtitle}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-briefcase me-1"></i>-
                              </div>
                            )}
                            {user.city_name ? (
                              <div className="text-muted">
                                <i className="bi bi-geo-alt me-1"></i>{user.city_name}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-geo-alt me-1"></i>-
                              </div>
                            )}
                            {user.department ? (
                              <div className="text-muted">
                                <i className="bi bi-building me-1"></i>{user.department}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-building me-1"></i>-
                              </div>
                            )}
                            {user.employee_group ? (
                              <div className="text-muted">
                                <i className="bi bi-people me-1"></i>{user.employee_group}
                              </div>
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-people me-1"></i>-
                              </div>
                            )}
                            {user.comment && (
                              <div className="text-muted mt-1 p-1 bg-white rounded border border-info">
                                <i className="bi bi-chat-text-fill text-info me-1"></i>
                                <small className="fst-italic">{user.comment}</small>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-1 ms-2">
                          <button 
                            type="button" 
                            className="btn btn-link btn-sm p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnregisterUser(user.User_ID);
                            }}
                            title="Remove user from roulette"
                          >
                            <i className="bi bi-trash-fill text-danger"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Selection Info */}
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <small>
                    <span className="badge bg-primary">{selectedUsers.length}</span> user(s) selected
                    {selectedUsers.length > data.roulette.group_size && (
                      <span className="text-warning ms-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Exceeds recommended size ({data.roulette.group_size})
                      </span>
                    )}
                  </small>
                  <button 
                    className="btn btn-sm btn-outline-secondary"
                    onClick={clearUserSelections}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowUnmatchedUsersModal(false);
                    setSelectedUsers([]);
                    if (modalMode === 'add') setSelectedGroup(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className={`btn ${modalMode === 'create' ? 'btn-success' : 'btn-primary'}`}
                  onClick={modalMode === 'create' ? handleCreateGroup : handleAddMember}
                  disabled={
                    (modalMode === 'create' && (creatingGroup || selectedUsers.length < 2)) ||
                    (modalMode === 'add' && (addingMember || !selectedGroup || selectedUsers.length === 0))
                  }
                >
                  {modalMode === 'create' ? (
                    creatingGroup ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-people-fill me-1"></i>Create Group
                      </>
                    )
                  ) : (
                    addingMember ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-plus-fill me-1"></i>Add Users
                      </>
                    )
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Modal */}
      {showDeleteGroupModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold text-danger">
                  <i className="bi bi-trash-fill me-2"></i>
                  Delete Group #{selectedGroupDisplayNumber}
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteGroupModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3 py-2">
                  <small>
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Members will be moved back to unmatched users
                  </small>
                </div>
                
                {selectedGroup && data?.matches.find(g => g.Group_ID === selectedGroup) && (
                  <div className="bg-light rounded p-2">
                    <small className="text-muted d-block mb-2">Members:</small>
                    {data.matches
                      .find(g => g.Group_ID === selectedGroup)
                      ?.members.map(member => (
                        <div key={member.User_ID} className="mb-1">
                          <small>• {member.firstname} {member.lastname}</small>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setSelectedGroup(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={handleDeleteGroup}
                  disabled={deletingGroup || !selectedGroup}
                >
                  {deletingGroup ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash-fill me-1"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-file-earmark-excel me-2 text-success"></i>
                  Export to Excel
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowExportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3 py-2">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Export all roulette data to Excel file
                  </small>
                </div>
                
                <small className="text-muted d-block mb-2">Export includes:</small>
                <ul className="small mb-0">
                  <li>Roulette configuration</li>
                  <li>All groups and members</li>
                  <li>Unmatched users</li>
                  <li>Registration details</li>
                </ul>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-success" 
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-1"></i>
                      Export
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Matches Modal */}
      {showSendMatchesModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-envelope-fill me-2 text-warning"></i>
                  Send Match Emails
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowSendMatchesModal(false)}
                  disabled={sendingMatches}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3 py-2">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Send bilingual emails to all participants
                  </small>
                </div>
                
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <div className="p-2 bg-light rounded text-center">
                      <small className="text-muted d-block">Total Groups</small>
                      <strong>{data.matches.length}</strong>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-2 bg-light rounded text-center">
                      <small className="text-muted d-block">Pending</small>
                      <strong className="text-warning">{data.matches.filter(g => !g.mail_sent).length}</strong>
                    </div>
                  </div>
                </div>
                
                {data.matches.filter(g => !g.mail_sent).length === 0 ? (
                  <div className="alert alert-success mb-0 py-2">
                    <small>
                      <i className="bi bi-check-circle-fill me-1"></i>
                      All groups have received emails
                    </small>
                  </div>
                ) : (
                  <p className="text-muted small mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    Only groups without emails will be notified ({data.matches.filter(g => !g.mail_sent).reduce((sum, group) => sum + group.members.length, 0)} recipients)
                  </p>
                )}
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowSendMatchesModal(false)}
                  disabled={sendingMatches}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={handleSendMatches}
                  disabled={sendingMatches || data.matches.filter(g => !g.mail_sent).length === 0}
                >
                  {sendingMatches ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-1"></i>
                      Send Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-chat-text-fill me-2 text-primary"></i>
                  {currentComment?.userName}
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowCommentModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="bg-light rounded p-3">
                  <p className="mb-0 small">{currentComment?.comment}</p>
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={() => setShowCommentModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold text-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {confirmAction?.title || 'Confirm Action'}
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle text-warning fs-3 me-3 flex-shrink-0"></i>
                  <div>
                    <p className="mb-0 small">{confirmAction?.message || 'Are you sure you want to proceed?'}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={async () => {
                    setShowConfirmModal(false);
                    if (confirmAction?.onConfirm) {
                      await confirmAction.onConfirm();
                    }
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registered Users Modal */}
      {showRegisteredUsersModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-people me-2 text-primary"></i>
                  Registered Users ({data?.registered_count || 0})
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowRegisteredUsersModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <button 
                  className="btn btn-primary btn-sm w-100 mb-3"
                  onClick={() => {
                    setShowRegisteredUsersModal(false);
                    setShowRegisterUserModal(true);
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Register New User
                </button>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {/* Show matched users */}
                  {data?.matches && data.matches.map((group, groupIndex) => 
                    group.members.map(member => (
                      <div key={member.User_ID} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                        <div>
                          <div className="fw-bold small">{member.firstname} {member.lastname}</div>
                          <small className="text-muted">{member.mail}</small>
                          <div><span className="badge bg-success mt-1">Group {groupIndex + 1}</span></div>
                        </div>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleUnregisterUser(member.User_ID)}
                        >
                          <i className="bi bi-person-dash"></i>
                        </button>
                      </div>
                    ))
                  )}
                  
                  {/* Show unmatched users */}
                  {data?.unmatched && data.unmatched.map(user => (
                    <div key={user.User_ID} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                      <div>
                        <div className="fw-bold small">{user.firstname} {user.lastname}</div>
                        <small className="text-muted">{user.mail}</small>
                        <div><span className="badge bg-warning text-dark mt-1">Unmatched</span></div>
                      </div>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleUnregisterUser(user.User_ID)}
                      >
                        <i className="bi bi-person-dash"></i>
                      </button>
                    </div>
                  ))}
                  
                  {(!data?.matches || data.matches.length === 0) && (!data?.unmatched || data.unmatched.length === 0) && (
                    <div className="text-center text-muted p-4">
                      <i className="bi bi-inbox fs-1"></i>
                      <p className="mt-2 small">No registered users yet</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowRegisteredUsersModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register User Modal */}
      {showRegisterUserModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-person-plus me-2 text-success"></i>
                  Register New User
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowRegisterUserModal(false);
                    setUserSearchInput('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <input 
                    type="text"
                    id="userSearch"
                    className="form-control form-control-sm"
                    placeholder="Search by name or email..."
                    value={userSearchInput}
                    onChange={(e) => setUserSearchInput(e.target.value)}
                  />
                </div>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredUnregisteredUsers.length > 0 ? (
                    filteredUnregisteredUsers.map(user => (
                      <div key={user.User_ID} className="mb-2 p-2 bg-light rounded border">
                        <div className="d-flex align-items-start">
                          {/* Profile Picture */}
                          {user.picture ? (
                            <img 
                              className="rounded-circle me-2" 
                              src={`data:image/png;base64,${user.picture}`} 
                              alt={`${user.firstname} ${user.lastname}`} 
                              style={{ width: '2.5rem', height: '2.5rem', objectFit: 'cover' }} 
                            />
                          ) : (
                            <div 
                              className="rounded-circle me-2 d-flex align-items-center justify-content-center bg-secondary text-white" 
                              style={{ width: '2.5rem', height: '2.5rem', fontSize: '0.75rem' }}
                            >
                              <i className="bi bi-person-fill"></i>
                            </div>
                          )}

                          {/* User Info */}
                          <div className="flex-grow-1">
                            <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>
                              {user.firstname} {user.lastname}
                            </div>
                            <div style={{fontSize: '0.75rem', lineHeight: '1.4'}}>
                              {user.mail ? (
                                <div className="text-muted">
                                  <i className="bi bi-envelope me-1"></i>{user.mail}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <i className="bi bi-envelope me-1"></i>-
                                </div>
                              )}
                              {user.jobtitle ? (
                                <div className="text-muted">
                                  <i className="bi bi-briefcase me-1"></i>{user.jobtitle}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <i className="bi bi-briefcase me-1"></i>-
                                </div>
                              )}
                              {user.city_name ? (
                                <div className="text-muted">
                                  <i className="bi bi-geo-alt me-1"></i>{user.city_name}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <i className="bi bi-geo-alt me-1"></i>-
                                </div>
                              )}
                              {user.department ? (
                                <div className="text-muted">
                                  <i className="bi bi-building me-1"></i>{user.department}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <i className="bi bi-building me-1"></i>-
                                </div>
                              )}
                              {user.employee_group ? (
                                <div className="text-muted">
                                  <i className="bi bi-people me-1"></i>{user.employee_group}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <i className="bi bi-people me-1"></i>-
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex gap-1 ms-2">
                            {user.comment && (
                              <button 
                                type="button" 
                                className="btn btn-link btn-sm p-0 me-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCommentModal(`${user.firstname} ${user.lastname}`, user.comment || '');
                                }}
                                title="View comment"
                              >
                                <i className="bi bi-chat-text-fill text-primary"></i>
                              </button>
                            )}
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => handleRegisterUser(user.User_ID)}
                            >
                              <i className="bi bi-person-plus"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted p-4">
                      <i className="bi bi-search fs-1"></i>
                      <p className="mt-2 small">
                        {userSearchInput ? 'No users found' : 'All users registered'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowRegisterUserModal(false);
                    setUserSearchInput('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Invitation Modal */}
      {showInvitationModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded">
              <div className="modal-header bg-light border-0">
                <h6 className="modal-title fw-bold">
                  <i className="bi bi-download me-2 text-primary"></i>
                  Download Invitation
                </h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowInvitationModal(false);
                    setAdditionalInfoDe('');
                    setAdditionalInfoEn('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3 py-2">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Download bilingual EML file to send via email client
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="additionalInfoDe" className="form-label small fw-bold mb-1">
                    Additional Info (German) <span className="text-muted fw-normal">(Optional)</span>
                  </label>
                  <textarea
                    id="additionalInfoDe"
                    className="form-control form-control-sm"
                    rows={3}
                    value={additionalInfoDe}
                    onChange={(e) => setAdditionalInfoDe(e.target.value)}
                    placeholder="Zusätzliche Informationen..."
                  ></textarea>
                </div>

                <div className="mb-0">
                  <label htmlFor="additionalInfoEn" className="form-label small fw-bold mb-1">
                    Additional Info (English) <span className="text-muted fw-normal">(Optional)</span>
                  </label>
                  <textarea
                    id="additionalInfoEn"
                    className="form-control form-control-sm"
                    rows={3}
                    value={additionalInfoEn}
                    onChange={(e) => setAdditionalInfoEn(e.target.value)}
                    placeholder="Additional information..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowInvitationModal(false);
                    setAdditionalInfoDe('');
                    setAdditionalInfoEn('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleDownloadInvitation}
                  disabled={downloadingInvitation}
                >
                  {downloadingInvitation ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-1"></i>
                      Download
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LunchRouletteAdminPanel;
